import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  lastValueFrom,
  Observable,
  Subject,
} from 'rxjs';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { OrderformPaymentProvider, OrderformState } from '../../../index';
import { ANIMATION_slideInOut } from '../../animations';
import { Store } from '@ngrx/store';
import { ToasterService } from '@digistore/ds24-ui/toaster';

export const PaymentProviderDataValidator = (control) => {
  return control.value.valid === true ? null : { payment: true };
};

@Component({
  selector: 'orderform-payment-methods',
  templateUrl: './payment-methods.component.html',
  styleUrls: ['./payment-methods.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [ANIMATION_slideInOut],
})
export class PaymentMethodsComponent implements OnInit, OnDestroy {
  private _textColor;
  private _placeholderColor;
  private _inputFieldColor;

  private sanitizeCssColorInput(colorOrVariable: string) {
    // Find occurences of CSS Variables: "var( ... )"
    const cssVarRegex = /var\((.+)\)/g;
    const resolveColor = (cssVariableName) => {
      return getComputedStyle(document.documentElement).getPropertyValue(
        cssVariableName
      );
    };

    // check if input value is a css variable
    const result = cssVarRegex.exec(colorOrVariable);
    return result?.[1] ? resolveColor(result[1]) : colorOrVariable;
  }

  @Input()
  public set textColor(colorOrVariable: string) {
    this._textColor = this.sanitizeCssColorInput(colorOrVariable);
  }
  public get textColor() {
    return this._textColor;
  }

  @Input()
  public set placeholderColor(colorOrVariable: string) {
    this._placeholderColor = this.sanitizeCssColorInput(colorOrVariable);
  }
  public get placeholderColor() {
    return this._placeholderColor;
  }

  @Input()
  public set inputFieldColor(colorOrVariable: string) {
    this._inputFieldColor = this.sanitizeCssColorInput(colorOrVariable);
  }
  public get inputFieldColor() {
    return this._inputFieldColor;
  }

  @Input() mode: 'vertical' | 'horizontal' = 'vertical';
  @Input() showCostsHint: 'true' | 'false' = 'true';

  @Input() fontSize;
  @Input() paymentIconsTheme: 'light' | 'dark' = 'dark';

  private _destroy$ = new Subject<void>();
  private _blockSendUpdate = false;

  private _paymentProviderNotAvailable$ = new BehaviorSubject<string[]>([]);

  private _paymentMethodList$ = combineLatest([
    this._orderformFascade.order$,
    this._paymentProviderNotAvailable$,
  ]).pipe(
    map(([orderformData, notAvaiable]) => {
      return orderformData.payment.filter(
        (provider) => notAvaiable.indexOf(provider.payment_provider_id) < 0
      );
    }),
    shareReplay(1)
  );

  /**
   * Enthält nur Bezahlmethoden die auch ausgewählt werden können.
   * z.B. void oder alias werden vorausgewählt vom Backend und können
   * nicht mehr geändert werden. In dem Fall werden die bestehenden
   * nur als Platzhalter angezegit und deaktiviert.
   */
  paymentMethodSelectionList$: Observable<OrderformPaymentProvider[]> =
    this._paymentMethodList$.pipe(
      map((paymentMethodList) =>
        paymentMethodList.filter(
          (method) =>
            method.pay_method !== 'void' && method.pay_method !== 'alias'
        )
      ),
      map((availablePayments: OrderformPaymentProvider[]) => {
        const sizeFactor = 1.3;
        return availablePayments.map((payment) => {
          return {
            ...payment,
            icon_data: {
              ...payment.icon_data,
              height: payment.icon_data.height * sizeFactor,
              width: payment.icon_data.width * sizeFactor,
              large: payment.icon_data.large
                ? {
                    ...payment.icon_data.large,
                    height: payment.icon_data.large.height * sizeFactor,
                    width: payment.icon_data.large.width * sizeFactor,
                  }
                : null,
              small: payment.icon_data.small
                ? {
                    ...payment.icon_data.small,
                    height: payment.icon_data.small.height * sizeFactor,
                    width: payment.icon_data.small.width * sizeFactor,
                  }
                : null,
            },
          };
        });
      })
    );

  selectedPaymentMethod$ = this._orderformFascade.order$.pipe(
    map((e) => {
      return e.order.payment_provider;
    }),
    shareReplay(1)
  );

  formGroup = new UntypedFormGroup({
    providerId: new UntypedFormControl(),
    providerData: new UntypedFormControl(
      { valid: true },
      PaymentProviderDataValidator
    ),
  });

  /**
   * Es gibt 2 Breakpoints:
   * 210 Pixel benötigt eine Option mindestens in der large-Variante
   * 110 Pixel werden mindestens für die normal-Variante benötigt
   * bp-normal: Tritt ein Wenn nicht genügend Platz mehr da ist für die large Variante
   * bp-small: Wenn nicht mehr Platz da ist für die normal Variante
   * (im css wird dann als Regel .mode-small.normal verwendet)
   */
  breakpointConfiguration$ = this.paymentMethodSelectionList$.pipe(
    map((list) => {
      return {
        [list.length * 210]: 'bp-normal',
        [list.length * 110]: 'bp-small',
      };
    })
  );

  private _subscriptionBeforeSubmit =
    this._orderformFascade.formValidation.subscribe(() =>
      this._onBeforeSubmit()
    );

  private _formValidationSubscription = this.formGroup.statusChanges
    .pipe(
      withLatestFrom(this.selectedPaymentMethod$),
      map(([, selectedMethod]) => {
        return (
          !(selectedMethod.disabled && selectedMethod.disabled_message) &&
          this.formGroup.valid
        );
      })
    )
    .subscribe((valid) => this.validate.emit({ valid, source: 'change' }));

  @Output() readonly validate = new EventEmitter<{
    valid: boolean;
    source: 'change' | 'submit';
  }>();

  private _submitFailedSubscription = this._orderformFascade.submitFailed
    .pipe(withLatestFrom(this.selectedPaymentMethod$))
    .subscribe(([, selectedMethod]) => {
      const valid =
        !(selectedMethod.disabled && selectedMethod.disabled_message) &&
        !(selectedMethod.status && selectedMethod.status.message) &&
        this.formGroup.valid;
      this.validate.emit({ valid, source: 'submit' });
    });

  constructor(
    private _orderformFascade: OrderformFascade,
    private _cdr: ChangeDetectorRef,
    private _store: Store<OrderformState>,
    private _ngZone: NgZone,
    private _toaster: ToasterService
  ) {
    this.selectedPaymentMethod$
      .pipe(
        distinctUntilChanged(
          (a, b) => a.payment_provider_id === b.payment_provider_id
        )
      )
      .pipe(withLatestFrom(this._paymentMethodList$), takeUntil(this._destroy$))
      .subscribe(([selectedPaymentProvider, availablePaymentMethods]) => {
        this._blockSendUpdate = true;
        this.formGroup
          .get('providerId')
          .patchValue(selectedPaymentProvider.payment_provider_id);
        this.formGroup.get('providerData').patchValue({ valid: true });
        this.formGroup.updateValueAndValidity();
        this._blockSendUpdate = false;
        if (
          this._paymentProviderNotAvailable$.value.indexOf(
            selectedPaymentProvider.payment_provider_id
          ) >= 0
        ) {
          this.formGroup
            .get('providerId')
            .patchValue(availablePaymentMethods[0].payment_provider_id);
        }
      });

    this.formGroup
      .get('providerId')
      .valueChanges.pipe(
        filter(() => !this._blockSendUpdate),
        takeUntil(this._destroy$),
        // complete selected payment plan by selected payment id
        withLatestFrom(this._paymentMethodList$)
      )
      .subscribe(([providerId, methodList]) => {
        const selectedPaymentProvider = methodList.find(
          (method) => method.payment_provider_id === providerId
        );
        if (selectedPaymentProvider) {
          // set payment plan to store
          this._orderformFascade.setOrderStore({
            payment_provider: selectedPaymentProvider,
          });
        }
      });
    this.formGroup
      .get('providerData')
      .valueChanges.pipe(
        filter(() => !this._blockSendUpdate),
        takeUntil(this._destroy$)
      )
      .subscribe((providerData) => {
        const data = { ...providerData };
        delete data['valid'];
        this._orderformFascade.setOrderStore({
          payment_provider_meta: data,
        });
      });
  }

  ngOnInit(): void {}

  trackByRendererType(index: number, provider: OrderformPaymentProvider) {
    return provider.renderer_type;
  }

  public addProviderToInvisibleList(provider_id: string) {
    this._paymentProviderNotAvailable$.next([
      provider_id,
      ...this._paymentProviderNotAvailable$.value,
    ]);
  }

  private async _onBeforeSubmit(): Promise<{ valid: boolean }> {
    let valid = true;
    const selectedMethod = await firstValueFrom(this.selectedPaymentMethod$);
    // Display message if amount is too high
    if (selectedMethod.disabled && selectedMethod.disabled_message) {
      this._toaster.error(selectedMethod.disabled_message);
      valid = false;
    }

    if (selectedMethod.status?.message) {
      valid = false;
    }

    return Promise.resolve({ valid });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._subscriptionBeforeSubmit.unsubscribe();
    this._formValidationSubscription.unsubscribe();
    this._submitFailedSubscription.unsubscribe();
  }
}
